import React, { useState, useEffect } from 'react';
import { 
  Headphones, 
  Globe, 
  Play, 
  Pause, 
  Clock, 
  Star, 
  StarOff, 
  BookOpen, 
  Search, 
  ChevronRight, 
  SkipBack, 
  SkipForward,
  Volume2,
  List,
  Home,
  Library,
  User
} from 'lucide-react';
import './podcast.css';

const LinguaPod = () => {
  const [featuredPodcasts, setFeaturedPodcasts] = useState([]);
  const [recommendedPodcasts, setRecommendedPodcasts] = useState([]);
  const [popularPodcasts, setPopularPodcasts] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('Spanish');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentProgress, setCurrentProgress] = useState(0);

  // Languages a user might be learning
  const languages = ['Spanish', 'French', 'Japanese', 'German', 'Mandarin', 'Italian', 'Korean', 'Portuguese'];

  // Mock data - in a real app, this would come from an API
  useEffect(() => {
    // Simulating API call
    const getFeaturedPodcasts = () => {
      return [
        {
          id: 1,
          title: "Daily Spanish Conversations",
          host: "Maria Rodriguez",
          language: "Spanish",
          level: "Intermediate",
          duration: "22 min",
          imageUrl: "/image/guy10.jpeg",
          isFavorite: true,
          plays: 15420
        },
        {
          id: 2,
          title: "French Culture Today",
          host: "Pierre Dubois",
          language: "French",
          level: "Advanced",
          duration: "35 min",
          imageUrl: "/image/guy11.jpeg",
          isFavorite: false,
          plays: 8723
        },
        {
          id: 3,
          title: "Japanese for Beginners",
          host: "Yuki Tanaka",
          language: "Japanese",
          level: "Beginner",
          duration: "18 min",
          imageUrl: "/image/lady12.jpeg",
          isFavorite: true,
          plays: 10112
        },
        {
          id: 7,
          title: "Spanish Slang & Idioms",
          host: "Diego Morales",
          language: "Spanish",
          level: "Advanced",
          duration: "26 min",
          imageUrl: "/image/lady15.jpeg",
          isFavorite: false,
          plays: 12540
        }
      ];
    };

    const getRecommendedPodcasts = () => {
      return [
        {
          id: 4,
          title: "Spanish News Simplified",
          host: "Carlos Vega",
          language: "Spanish",
          level: "Intermediate",
          duration: "15 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: false,
          plays: 6284
        },
        {
          id: 5,
          title: "Learn Spanish Through Stories",
          host: "Elena Gomez",
          language: "Spanish",
          level: "Beginner",
          duration: "25 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: false,
          plays: 9342
        },
        {
          id: 6,
          title: "Business Spanish",
          host: "Miguel Santos",
          language: "Spanish",
          level: "Advanced",
          duration: "30 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: true,
          plays: 7521
        }
      ];
    };

    const getPopularPodcasts = () => {
      return [
        {
          id: 8,
          title: "Spanish with Juan",
          host: "Juan Martínez",
          language: "Spanish",
          level: "All Levels",
          duration: "40 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: false,
          plays: 24567
        },
        {
          id: 9,
          title: "Coffee Break Spanish",
          host: "María González",
          language: "Spanish",
          level: "Intermediate",
          duration: "20 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: true,
          plays: 18934
        },
        {
          id: 10,
          title: "Notes in Spanish",
          host: "Ben Curtis & Marina Diez",
          language: "Spanish",
          level: "Beginner",
          duration: "15 min",
          imageUrl: "/api/placeholder/120/120",
          isFavorite: false,
          plays: 16342
        }
      ];
    };

    setFeaturedPodcasts(getFeaturedPodcasts());
    setRecommendedPodcasts(getRecommendedPodcasts());
    setPopularPodcasts(getPopularPodcasts());

    // Simulate progress when a podcast is playing
    let interval;
    if (isPlaying && currentPodcast) {
      interval = setInterval(() => {
        setCurrentProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 0;
          }
          return prev + 0.5;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isPlaying, currentPodcast]);

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    // In a real app, this would trigger an API call to get podcasts for the selected language
  };

  const togglePlayPause = (podcast) => {
    if (currentPodcast && currentPodcast.id === podcast.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentPodcast(podcast);
      setCurrentProgress(0);
      setIsPlaying(true);
    }
  };

  const toggleFavorite = (podcastId) => {
    // Update all podcast lists
    setFeaturedPodcasts(featuredPodcasts.map(podcast => 
      podcast.id === podcastId ? {...podcast, isFavorite: !podcast.isFavorite} : podcast
    ));
    
    setRecommendedPodcasts(recommendedPodcasts.map(podcast => 
      podcast.id === podcastId ? {...podcast, isFavorite: !podcast.isFavorite} : podcast
    ));

    setPopularPodcasts(popularPodcasts.map(podcast => 
      podcast.id === podcastId ? {...podcast, isFavorite: !podcast.isFavorite} : podcast
    ));
  };

  const handleProgressChange = (e) => {
    setCurrentProgress(e.target.value);
  };

  // Podcast card component
  const PodcastCard = ({ podcast }) => (
    <div className="podcast-card">
      <div className="podcast-image">
        <img src={podcast.imageUrl} alt={podcast.title} />
        <button 
          className="play-button"
          onClick={(e) => {
            e.stopPropagation();
            togglePlayPause(podcast);
          }}
        >
          {currentPodcast && currentPodcast.id === podcast.id && isPlaying ? 
            <Pause size={20} /> : <Play size={20} />}
        </button>
        <button 
          className="favorite-button"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(podcast.id);
          }}
        >
          {podcast.isFavorite ? 
            <Star size={16} className="favorite-icon-active" /> : 
            <StarOff size={16} className="favorite-icon" />}
        </button>
      </div>
      <div className="podcast-info">
        <h3>{podcast.title}</h3>
        <p className="podcast-host">{podcast.host}</p>
        <div className="podcast-details">
          <span className="podcast-level">{podcast.level}</span>
          <div className="podcast-duration">
            <Clock size={12} />
            {podcast.duration}
          </div>
        </div>
      </div>
    </div>
  );

  // Row of podcasts component
  const PodcastRow = ({ title, icon, podcasts, showAll }) => (
    <section className="podcast-row">
      <div className="section-header">
        <div className="section-title">
          {icon}
          <h2>{title}</h2>
        </div>
        {showAll && (
          <button className="see-all-button">
            See all <ChevronRight size={16} />
          </button>
        )}
      </div>
      <div className="podcast-grid">
        {podcasts.map(podcast => (
          <PodcastCard key={podcast.id} podcast={podcast} />
        ))}
      </div>
    </section>
  );

  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <Headphones size={28} />
          <h1>LinguaPod</h1>
        </div>
        
        <nav className="main-nav">
          <ul>
            <li>
              <button className="nav-button active">
                <Home size={20} />
                Home
              </button>
            </li>
            <li>
              <button className="nav-button">
                <Search size={20} />
                Search
              </button>
            </li>
            <li>
              <button className="nav-button">
                <Library size={20} />
                Your Library
              </button>
            </li>
            <li>
              <button className="nav-button">
                <Globe size={20} />
                Languages
              </button>
            </li>
          </ul>

          <div className="languages-section">
            <h3>Your Languages</h3>
            <ul>
              {languages.slice(0, 4).map(lang => (
                <li key={lang}>
                  <button 
                    className={`language-link ${currentLanguage === lang ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang)}
                  >
                    {lang}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="profile-section">
          <button>
            <User size={20} />
            Profile
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <header className="main-header">
          <div className="header-top">
            <div className="mobile-logo">
              <Headphones size={24} />
              <h1>LinguaPod</h1>
            </div>
            <div className="search-container">
              <div className="search-input-container">
                <input
                  type="text"
                  placeholder="Search podcasts, hosts, or episodes"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search size={18} className="search-icon" />
              </div>
            </div>
            <button className="mobile-menu">
              <List size={24} />
            </button>
          </div>
          
          <div className="language-tabs">
            {languages.map(language => (
              <button 
                key={language} 
                className={`language-tab ${currentLanguage === language ? 'active' : ''}`}
                onClick={() => handleLanguageChange(language)}
              >
                {language}
              </button>
            ))}
          </div>
        </header>

        {/* Main content */}
        <div className="content-area">
          <div className="welcome-section">
            <h2>Welcome Back</h2>
            <div className="recent-grid">
              {featuredPodcasts.slice(0, 4).map(podcast => (
                <div 
                  key={podcast.id}
                  className="recent-item"
                  onClick={() => togglePlayPause(podcast)}
                >
                  <img src={podcast.imageUrl} alt={podcast.title} />
                  <div className="recent-info">
                    <h3>{podcast.title}</h3>
                    <p>{podcast.host}</p>
                  </div>
                  <button 
                    className="recent-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlayPause(podcast);
                    }}
                  >
                    {currentPodcast && currentPodcast.id === podcast.id && isPlaying ? 
                      <Pause size={20} /> : <Play size={20} />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <PodcastRow 
            title="Featured for You" 
            icon={<Star size={20} />} 
            podcasts={featuredPodcasts} 
            showAll
          />
          
          <PodcastRow 
            title={`${currentLanguage} Recommendations`} 
            icon={<BookOpen size={20} />} 
            podcasts={recommendedPodcasts} 
            showAll
          />
          
          <PodcastRow 
            title="Popular with Learners" 
            icon={<Headphones size={20} />} 
            podcasts={popularPodcasts} 
            showAll
          />
        </div>
      </div>

      {/* Player bar */}
      {currentPodcast && (
        <div className="player-bar">
          <div className="player-content">
            <div className="now-playing">
              <img 
                src={currentPodcast.imageUrl} 
                alt={currentPodcast.title}
              />
              <div className="now-playing-info">
                <h4>{currentPodcast.title}</h4>
                <p>{currentPodcast.host}</p>
              </div>
            </div>
            
            <div className="player-center">
              <div className="player-controls">
                <button className="control-button">
                  <SkipBack size={18} />
                </button>
                <button 
                  className="play-pause-button"
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <button className="control-button">
                  <SkipForward size={18} />
                </button>
              </div>
              
              <div className="progress-container">
                <span className="time">
                  {Math.floor(currentProgress / 100 * 
                    parseInt(currentPodcast.duration.split(' ')[0])).toString().padStart(2, '0')}:
                    {Math.floor((currentProgress / 100 * 
                    parseInt(currentPodcast.duration.split(' ')[0]) % 1) * 60).toString().padStart(2, '0')}
                </span>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={currentProgress} 
                  onChange={handleProgressChange}
                  className="progress-bar"
                />
                <span className="time">
                  {currentPodcast.duration.split(' ')[0]}:00
                </span>
              </div>
            </div>
            
            <div className="player-right">
              <div className="extra-controls">
                <button 
                  className="icon-button"
                  onClick={() => toggleFavorite(currentPodcast.id)}
                >
                  {currentPodcast.isFavorite ? 
                    <Star size={18} className="favorite-active" /> : 
                    <StarOff size={18} />}
                </button>
                <button className="icon-button volume-button">
                  <Volume2 size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinguaPod;